/**
 * Manages WhatsApp templates for the New Message modal.
 * WhatsApp requires pre-approved templates for proactive outreach (Meta policy).
 * All templates are shown with status indicators; non-approved templates may fail at send time.
 */

import { useCallback, useMemo } from 'react';

import { UseFormReturn, useWatch } from 'react-hook-form';

import { useDeskId } from '@/hooks/useDeskId';
import { useWhatsAppTemplates } from '@/modules/whatsapp/hooks/useWhatsAppTemplates';
import {
  getComponentsFromTemplate,
  getVariablesLength,
  isMediaHeader,
} from '@/modules/whatsapp/utils';
import { Integration, IntegrationType } from '@/shared/types/integration';

import { NewMessageFormValues } from './useNewMessageForm';

interface UseNewMessageTemplatesProps {
  form: UseFormReturn<NewMessageFormValues>;
  selectedIntegration: Integration | null;
  selectedIntegrationType: IntegrationType | null;
  headerReplace: (values: { name: string }[]) => void;
  bodyReplace: (values: { name: string }[]) => void;
  footerReplace: (values: { name: string }[]) => void;
}

export const useNewMessageTemplates = ({
  form,
  selectedIntegration,
  selectedIntegrationType,
  headerReplace,
  bodyReplace,
  footerReplace,
}: UseNewMessageTemplatesProps) => {
  const deskId = useDeskId();
  const { control, setValue, clearErrors } = form;
  const selectedIntegrationId = selectedIntegration?.integration_id || null;

  const { templates: whatsAppTemplates, status: templatesStatus } = useWhatsAppTemplates({
    deskId,
    integrationId: selectedIntegrationId,
    enabled: selectedIntegrationType === IntegrationType.WHATSAPP,
  });

  const templates = useMemo(() => whatsAppTemplates || [], [whatsAppTemplates]);

  const templateId = useWatch({ control, name: 'templateId' });

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) ?? null,
    [templateId, templates]
  );

  const templateComponents = useMemo(
    () => (selectedTemplate ? getComponentsFromTemplate(selectedTemplate) : null),
    [selectedTemplate]
  );

  const headerVariablesCount = templateComponents?.header
    ? getVariablesLength(templateComponents.header)
    : 0;

  const bodyVariablesCount = templateComponents?.body
    ? getVariablesLength(templateComponents.body)
    : 0;

  const footerVariablesCount = templateComponents?.footer
    ? getVariablesLength(templateComponents.footer)
    : 0;

  const showMediaHeader = Boolean(
    templateComponents?.header && isMediaHeader(templateComponents.header)
  );

  /** Clears existing variable fields and creates new empty fields matching the selected template. */
  const onTemplateChange = useCallback(
    (newTemplateId: string | null) => {
      setValue('templateId', newTemplateId, { shouldDirty: true });

      headerReplace([]);
      bodyReplace([]);
      footerReplace([]);
      setValue('headerMediaParam', null);
      clearErrors();

      if (!newTemplateId) {
        return;
      }

      const newTemplate = templates.find((t) => t.id === newTemplateId);
      if (!newTemplate) {
        return;
      }

      const { header, body, footer } = getComponentsFromTemplate(newTemplate);
      const headerVarsCount = getVariablesLength(header);
      const bodyVarsCount = getVariablesLength(body);
      const footerVarsCount = getVariablesLength(footer);

      if (headerVarsCount > 0 && !isMediaHeader(header)) {
        headerReplace(Array.from({ length: headerVarsCount }, () => ({ name: '' })));
      }
      if (bodyVarsCount > 0) {
        bodyReplace(Array.from({ length: bodyVarsCount }, () => ({ name: '' })));
      }
      if (footerVarsCount > 0) {
        footerReplace(Array.from({ length: footerVarsCount }, () => ({ name: '' })));
      }
    },
    [setValue, templates, headerReplace, bodyReplace, footerReplace, clearErrors]
  );

  return {
    templates,
    isTemplatesLoading: templatesStatus === 'pending',
    selectedTemplate,
    templateComponents,
    headerVariablesCount,
    bodyVariablesCount,
    footerVariablesCount,
    showMediaHeader,
    onTemplateChange,
  };
};
